import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Automation } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import {
  allAutomationsOptions,
  errorMessage,
  type AutomationListItem,
} from '@/features/desks/desks-queries';
import {
  ACTION_LABELS,
  AutomationDialog,
  automationBodyFrom,
} from '@/features/desks/automation-dialog';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { normalizeText, SettingsBreadcrumb, SettingsSearch } from '@/features/settings/settings-ui';

/** Rótulo da coluna Tipo: a primeira ação da automação, como no TiFlux. */
function typeLabel(a: AutomationListItem): string {
  const first = a.actions[0];
  return first ? ACTION_LABELS[first.type] : '—';
}

/**
 * /settings/desks/automations — automações de TODAS as mesas (e globais) no
 * padrão TiFlux: tabela Nome | Tipo | Mesas, switch de ativo e editar/excluir.
 * A ordem é a do servidor (mesa asc, 'Todas' no fim); a busca só filtra.
 */
export function AutomationsSettingsPage() {
  const queryClient = useQueryClient();
  const automations = useQuery(allAutomationsOptions());
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ automation: AutomationListItem | null } | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['erp', 'automations'] });

  const toggle = useMutation({
    mutationFn: async (a: AutomationListItem) => {
      // Linha FRESCA antes do PUT: o corpo substitui o registro inteiro — um
      // body montado do cache apagaria edições feitas por outro usuário.
      const rows = await queryClient.fetchQuery({ ...allAutomationsOptions(), staleTime: 0 });
      const fresh = rows.find((row) => row.id === a.id);
      if (!fresh) throw new Error('Automação não encontrada — atualize a lista.');
      const res = await api<Automation>(`/automations/${a.id}`, {
        method: 'PUT',
        body: automationBodyFrom(fresh, !a.active),
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (automationId: number) => {
      const res = await api<void>(`/automations/${automationId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  // Haystack normalizado pré-computado por linha — a busca só filtra, sem
  // re-sortear (a ordem vem do servidor).
  const rows = useMemo(
    () =>
      (automations.data ?? []).map((a) => ({
        row: a,
        haystack: normalizeText(`${a.name} ${typeLabel(a)} ${a.deskName ?? 'Todas'}`),
      })),
    [automations.data]
  );
  const q = normalizeText(query.trim());
  const list = useMemo(
    () => (q === '' ? rows : rows.filter((r) => r.haystack.includes(q))).map((r) => r.row),
    [rows, q]
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Automações" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Automações</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar automação" />
          <Button size="sm" className="h-9" onClick={() => setDialog({ automation: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Automação
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {automations.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : automations.isError ? (
          <EmptyState
            message={`Erro ao carregar automações: ${errorMessage(automations.error)}`}
          />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhuma automação encontrada' : 'Nenhuma automação cadastrada'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Tipo</th>
                  <th className="h-10 px-4 font-semibold">Mesas</th>
                  <th className="h-10 w-32 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr
                    key={a.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-80 truncate px-4 font-medium">{a.name}</td>
                    <td className="px-4 text-muted-foreground whitespace-nowrap">
                      {typeLabel(a)}
                    </td>
                    <td className="max-w-56 truncate px-4">{a.deskName ?? 'Todas'}</td>
                    <td className="px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <Switch
                          checked={a.active}
                          disabled={toggle.isPending}
                          onCheckedChange={() => toggle.mutate(a)}
                          aria-label={a.active ? `Desativar "${a.name}"` : `Ativar "${a.name}"`}
                        />
                        <DeskRowAction
                          label="Editar automação"
                          onClick={() => setDialog({ automation: a })}
                        >
                          <Pencil className="size-3.5" />
                        </DeskRowAction>
                        <DeskRowAction
                          label="Excluir automação"
                          destructive
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir a automação "${a.name}"?`)) {
                              remove.mutate(a.id);
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </DeskRowAction>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {(toggle.error ?? remove.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(toggle.error ?? remove.error)}
        </p>
      ) : null}

      {dialog ? (
        <AutomationDialog automation={dialog.automation} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
